/**
 * 広告サービス（モック実装 + AdMob 互換 IF）。
 *
 * 設計:
 *   - 公開 API はリワード用の `showRewardedAd(slot)` と、バナー用の
 *     `showBannerAd()` / `hideBannerAd()`。
 *   - 表示開始で `setAdMuted(true)` + `suspendForAd()` を呼び、ゲーム BGM/SE を
 *     ダッキングする。終了 / キャンセル / 失敗のすべてで `setAdMuted(false)` +
 *     `resumeFromAd()` を保証（try/finally）。
 *   - 経路:
 *       * `getAdMode() === 'mock'` または Capacitor Native でない場合 → mock 経路
 *       * `getAdMode() === 'production'` かつ Capacitor Native かつ Ad Unit ID 設定済
 *         → AdMob 経路（@capacitor-community/admob を dynamic import）
 *       * production モード指定だが ID 未設定 → console.warn + mock 経路に倒す
 *         （ID 発行前のビルドで「広告失敗で進行不能」を避けるための fallback:ok）
 *   - mock 経路は `<AdRewardedOverlay />` がマウント時に register したコールバック
 *     経由でオーバーレイを表示する。オーバーレイ未マウントなら `window.confirm` の
 *     簡易 UI にフォールバック（SSR / 起動直後の安全装置）。
 *   - キャンセル / 報酬未獲得時は `false` を返す。呼び出し側はこの戻り値で報酬付与の
 *     可否を判断する（CLAUDE.md 「サイレントフォールバック禁止」を遵守し、UI 側で
 *     失敗を明示通知できるようにする）。
 *
 * 参考実装:
 *   - balloon-float `src/services/admob.ts`（AdMob 経路の watchdog / リスナー管理）
 *   - balloon-float `src/services/ads.ts`（オーバーレイ register / mute callback）
 *
 * 関連ルール: `.claude/rules/antipatterns.md`, `.claude/rules/no-hardcode.md`
 */

import {
  AD_DISMISS_TIMEOUT_MS,
  AD_MOCK_CONFIRM_DELAY_MS,
  AD_PRELOAD_MAX_RETRY_COUNT,
  AD_PRELOAD_RETRY_BASE_MS,
  AD_PRELOAD_RETRY_MAX_MS,
  getBannerAdUnitId,
  getAdMode,
  getRewardedAdUnitId,
  getYahooRewardedTagId,
  type AdSlotType,
} from '../config/ads'
import { getPlatformId, isCapacitorNative } from '../utils/platform'
import { resumeFromAd, suspendForAd } from './audioContext'
import { setAdMuted } from './volume'
import { getAdRewardConfirmMessage } from './adRewardDisclosure'
import { trackAdReward } from './analytics'
import type { AdMobError, AdMobRewardItem } from '@capacitor-community/admob'

// ---------------------------------------------------------------------------
// オーバーレイ register/unregister（AdRewardedOverlay コンポーネントが呼ぶ）
// ---------------------------------------------------------------------------

export type RewardedOverlayCallback = (
  slot: AdSlotType,
  onClose: (rewarded: boolean) => void,
  yahooTagId?: string,
) => void

export type AdNekoPopupCallback = (
  slot: AdSlotType,
  onClose: (accepted: boolean) => void,
) => void

let registeredRewardedOverlay: RewardedOverlayCallback | null = null
let registeredAdNekoPopup: AdNekoPopupCallback | null = null
let admobInitialized = false
let admobConsentChecked = false
let admobInitInflight: Promise<boolean> | null = null
let admobRewardInflight: Promise<boolean> | null = null
let admobRewardPreloadInflight: Promise<boolean> | null = null
let admobRewardPreloaded = false
let admobRewardPreloadRetryCount = 0
let admobRewardPreloadRetryTimer: ReturnType<typeof setTimeout> | null = null
let admobBannerInflight: Promise<void> | null = null
let bannerTargetVisible = false
let bannerTargetPlacement: BannerPlacement = 'bottom'
let bannerAppliedVisible = false
let bannerAppliedPlacement: BannerPlacement = 'bottom'
let bannerSizeListener: { remove: () => Promise<void> } | null = null

declare global {
  interface Window {
    YJ_YADS?: {
      tasks: Array<{
        yads_ad_ds: string
        yads_parent_element: string
      }>
    }
  }
}

/** `<AdRewardedOverlay />` がマウント時に呼び、表示要求コールバックを登録する。 */
export function registerRewardedOverlay(cb: RewardedOverlayCallback): void {
  registeredRewardedOverlay = cb
}

/** unmount 時の cleanup。残ったまま再 register されると重複 resolve の原因になる。 */
export function unregisterRewardedOverlay(): void {
  registeredRewardedOverlay = null
}

/** 広告表示前の確認ポップアップを登録する。 */
export function registerAdNekoPopup(cb: AdNekoPopupCallback): void {
  registeredAdNekoPopup = cb
}

export function unregisterAdNekoPopup(): void {
  registeredAdNekoPopup = null
}

// ---------------------------------------------------------------------------
// 公開 API
// ---------------------------------------------------------------------------

/**
 * リワード動画広告を表示する。
 *
 * 戻り値:
 *   - `true`  : ユーザーが動画を最後まで視聴し、報酬を獲得した
 *   - `false` : 視聴を途中でやめた / 失敗した / 表示できなかった
 *
 * 呼び出し側はこの戻り値だけを見て報酬付与を判断する設計。
 * 失敗時のメッセージ表示は呼び出し元（現状は useStamina）が担当する。
 */
export async function showRewardedAd(slot: AdSlotType): Promise<boolean> {
  const watched = await requestRewardedAd(slot)
  // AdMob / Yahoo / mock のどの経路を通ったかに関わらず、報酬到達率をここで一本化する。
  // watched=false は「事前ポップアップで断った」「途中で閉じた」「表示に失敗した」の
  // 合算であり、GA4 側で区別はできない。
  trackAdReward(slot, watched)
  return watched
}

async function requestRewardedAd(slot: AdSlotType): Promise<boolean> {
  const accepted = await confirmBeforeAd(slot)
  if (!accepted) return false

  if (shouldUseAdMob()) {
    try {
      return await showAdMobRewarded(slot)
    } catch (err) {
      console.warn(`[ads] showRewardedAd unexpected AdMob error (slot=${slot}):`, err)
      return false
    }
  }

  // mute / suspend を try 内に置くことで、将来 suspendForAd が throw する変更が入っても
  // finally で必ず resume / unmute される（BGM 永続ミュート防止）。
  try {
    setAdMuted(true)
    await suspendForAd()
    if (shouldUseYahooAds()) {
      return await showYahooRewarded(slot)
    }
    return await showMockRewarded(slot)
  } catch (err) {
    // fallback:ok — ここに到達するのは想定外の例外パスのみ。広告表示失敗で
    // ゲームを止めず false を返し、呼び出し側で「広告再生に失敗しました」と表示する。
    console.warn(`[ads] showRewardedAd unexpected error (slot=${slot}):`, err)
    return false
  } finally {
    await resumeFromAd()
    setAdMuted(false)
  }
}

/**
 * 後方互換エイリアス。
 * 現行の利用元は `useStamina.ts`。
 */
export const showRewardedAdStub = showRewardedAd

export type BannerPlacement = 'top' | 'bottom'

/** Android/iOS のリワード広告を事前読み込みする。Web / mock / ID 未設定では no-op。 */
export async function preloadRewardedAd(): Promise<boolean> {
  if (!shouldUseAdMob()) return false
  if (admobRewardPreloaded) return true
  if (admobRewardPreloadInflight) return admobRewardPreloadInflight

  admobRewardPreloadInflight = preloadAdMobRewardedInternal().finally(() => {
    admobRewardPreloadInflight = null
  })
  return admobRewardPreloadInflight
}

/**
 * ネイティブ AdMob バナーを表示する。
 * Web / mock / ID 未設定では no-op。ゲーム画面側はフェーズだけで show/hide を呼べる。
 */
export async function showBannerAd(placement: BannerPlacement = 'bottom'): Promise<void> {
  bannerTargetVisible = true
  bannerTargetPlacement = placement
  await syncBannerAd()
}

/** ネイティブ AdMob バナーを隠す。非表示済み・Web では no-op。 */
export async function hideBannerAd(): Promise<void> {
  bannerTargetVisible = false
  await syncBannerAd()
}

/**
 * Google UMP のプライバシー設定フォームを表示する。
 * フォーム不要地域では plugin 側が reject することがあるため、呼び出し元には成否だけ返す。
 */
export async function showAdPrivacyOptions(): Promise<boolean> {
  if (!isCapacitorNative() || getAdMode() !== 'production') return false
  try {
    await initializeAdMobOnce()
    const { AdMob } = await import('@capacitor-community/admob')
    await AdMob.showPrivacyOptionsForm()
    return true
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'unknown'
    console.warn(`[ads] privacy options form unavailable: ${msg}`)
    return false
  }
}

function setBannerHeightCss(height: number): void {
  if (typeof document === 'undefined') return
  const normalized = Number.isFinite(height) && height > 0 ? Math.round(height) : 0
  document.documentElement.style.setProperty('--admob-banner-height', `${normalized}px`)
}

// ---------------------------------------------------------------------------
// 経路選択
// ---------------------------------------------------------------------------

function shouldUseAdMob(): boolean {
  if (!isCapacitorNative()) return false
  if (getAdMode() !== 'production') return false
  if (getRewardedAdUnitId().length === 0) {
    // production モード指定だが ID 未設定 → ビルド設定ミス。
    // ID 発行前のビルドで「広告失敗で進行不能」を避けるため mock に倒す。
    // 設定漏れに気づけるよう警告ログは error レベルで出す。
    console.error(
      '[ads] VITE_AD_MODE=production but VITE_ADMOB_REWARDED_ID is empty; ' +
        'falling back to mock overlay',
    )
    return false
  }
  return true
}

function shouldUseYahooAds(): boolean {
  return getPlatformId() === 'line_mini' && getAdMode() === 'production'
}

function shouldUseBannerAdMob(): boolean {
  if (!isCapacitorNative()) return false
  if (getAdMode() !== 'production') return false
  if (getBannerAdUnitId().length === 0) {
    console.error(
      '[ads] VITE_AD_MODE=production but VITE_ADMOB_BANNER_ID is empty; ' +
        'banner ad will not be shown',
    )
    return false
  }
  return true
}

// ---------------------------------------------------------------------------
// AdMob 経路（Capacitor Native + production + ID 設定済の時のみ）
// ---------------------------------------------------------------------------

async function showAdMobRewarded(slot: AdSlotType): Promise<boolean> {
  if (admobRewardInflight) return admobRewardInflight
  admobRewardInflight = showAdMobRewardedInternal(slot).finally(() => {
    admobRewardInflight = null
  })
  return admobRewardInflight
}

async function initializeAdMobOnce(): Promise<boolean> {
  if (admobInitialized) return true
  if (admobInitInflight) return admobInitInflight

  admobInitInflight = (async () => {
    const { AdMob } = await import('@capacitor-community/admob')

    if (!admobInitialized) {
      try {
        const { status } = await AdMob.trackingAuthorizationStatus()
        if (status === 'notDetermined') {
          await AdMob.requestTrackingAuthorization()
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'unknown'
        console.warn(`[ads] ATT status/request skipped: ${msg}`)
      }

      await AdMob.initialize({
        initializeForTesting: getAdMode() !== 'production',
      })
      admobInitialized = true
    }

    void refreshAdMobConsentInfo()
    return true
  })().finally(() => {
    admobInitInflight = null
  })

  return admobInitInflight
}

async function refreshAdMobConsentInfo(): Promise<void> {
  if (admobConsentChecked) return
  admobConsentChecked = true

  try {
    const { AdMob } = await import('@capacitor-community/admob')
    let consentInfo = await AdMob.requestConsentInfo({
      tagForUnderAgeOfConsent: false,
    })
    if (!consentInfo.canRequestAds && consentInfo.isConsentFormAvailable) {
      consentInfo = await AdMob.showConsentForm()
    }
    if (!consentInfo.canRequestAds) {
      console.warn(
        `[ads] AdMob consent did not report canRequestAds=true: status=${consentInfo.status}, ` +
          `privacyOptions=${consentInfo.privacyOptionsRequirementStatus}`,
      )
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'unknown'
    console.warn(`[ads] UMP consent flow skipped: ${msg}`)
  }
}

async function syncBannerAd(): Promise<void> {
  if (admobBannerInflight) return admobBannerInflight
  admobBannerInflight = syncBannerAdInternal().finally(() => {
    admobBannerInflight = null
  })
  return admobBannerInflight
}

async function syncBannerAdInternal(): Promise<void> {
  while (true) {
    const targetVisible = bannerTargetVisible
    const targetPlacement = bannerTargetPlacement

    if (targetVisible) {
      bannerAppliedVisible = await showAdMobBannerInternal(targetPlacement)
      if (bannerAppliedVisible) {
        bannerAppliedPlacement = targetPlacement
      }
    } else {
      await hideAdMobBannerInternal()
      bannerAppliedVisible = false
      setBannerHeightCss(0)
    }

    if (targetVisible === bannerTargetVisible && targetPlacement === bannerTargetPlacement) {
      return
    }
  }
}

async function showAdMobBannerInternal(placement: BannerPlacement): Promise<boolean> {
  if (
    bannerAppliedVisible &&
    bannerAppliedPlacement === placement &&
    shouldUseBannerAdMob()
  ) {
    return true
  }
  if (!shouldUseBannerAdMob()) return false

  try {
    const canRequestAds = await initializeAdMobOnce()
    if (!canRequestAds) return false
    const { AdMob, BannerAdPluginEvents, BannerAdPosition, BannerAdSize } = await import('@capacitor-community/admob')
    if (!bannerSizeListener) {
      bannerSizeListener = await AdMob.addListener(BannerAdPluginEvents.SizeChanged, (info) => {
        setBannerHeightCss(info.height)
      })
    }
    await AdMob.showBanner({
      adId: getBannerAdUnitId(),
      adSize: BannerAdSize.ADAPTIVE_BANNER,
      position: placement === 'top' ? BannerAdPosition.TOP_CENTER : BannerAdPosition.BOTTOM_CENTER,
      isTesting: getAdMode() !== 'production',
    })
    return true
  } catch (err) {
    setBannerHeightCss(0)
    console.warn(`[ads] AdMob banner failed (placement=${placement}):`, err)
    return false
  }
}

async function hideAdMobBannerInternal(): Promise<void> {
  if (!bannerAppliedVisible && !shouldUseBannerAdMob()) return
  if (!isCapacitorNative()) return

  try {
    const { AdMob } = await import('@capacitor-community/admob')
    await AdMob.hideBanner()
    bannerAppliedVisible = false
    setBannerHeightCss(0)
  } catch (err) {
    console.warn('[ads] AdMob banner hide failed:', err)
  }
}

async function preloadAdMobRewardedInternal(): Promise<boolean> {
  const canRequestAds = await initializeAdMobOnce()
  if (!canRequestAds) return false

  try {
    const { AdMob } = await import('@capacitor-community/admob')
    await AdMob.prepareRewardVideoAd({
      adId: getRewardedAdUnitId(),
      isTesting: getAdMode() !== 'production',
    })
    admobRewardPreloaded = true
    admobRewardPreloadRetryCount = 0
    return true
  } catch (err) {
    admobRewardPreloaded = false
    console.warn('[ads] AdMob rewarded preload failed:', err)
    scheduleRewardedPreloadRetry()
    return false
  }
}

function scheduleRewardedPreloadRetry(): void {
  if (admobRewardPreloadRetryTimer) return
  if (admobRewardPreloadRetryCount >= AD_PRELOAD_MAX_RETRY_COUNT) return

  const baseDelay = Math.min(
    AD_PRELOAD_RETRY_BASE_MS * 2 ** admobRewardPreloadRetryCount,
    AD_PRELOAD_RETRY_MAX_MS,
  )
  const jitter = baseDelay * 0.2 * (Math.random() * 2 - 1)
  const delay = Math.round(baseDelay + jitter)
  admobRewardPreloadRetryCount += 1

  admobRewardPreloadRetryTimer = setTimeout(() => {
    admobRewardPreloadRetryTimer = null
    void preloadRewardedAd()
  }, delay)
}

async function showAdMobRewardedInternal(slot: AdSlotType): Promise<boolean> {
  const canRequestAds = await initializeAdMobOnce()
  if (!canRequestAds) return false
  const { AdMob, RewardAdPluginEvents } = await import('@capacitor-community/admob')
  type AdListenerHandle = Awaited<ReturnType<typeof AdMob.addListener>>

  let rewarded = false
  let rewardListener: AdListenerHandle | null = null
  let dismissedListener: AdListenerHandle | null = null
  let failedToLoadListener: AdListenerHandle | null = null
  let failedToShowListener: AdListenerHandle | null = null
  let watchdogTimer: ReturnType<typeof setTimeout> | null = null
  let audioSuspended = false
  const restoreBanner = bannerTargetVisible

  try {
    if (admobRewardPreloadRetryTimer) {
      window.clearTimeout(admobRewardPreloadRetryTimer)
      admobRewardPreloadRetryTimer = null
    }
    const adId = getRewardedAdUnitId()
    const options = {
      adId,
      isTesting: getAdMode() !== 'production',
    }

    let settle!: (reason: string, error?: AdMobError) => void
    const finished = new Promise<void>((resolve) => {
      let settled = false
      settle = (reason: string, error?: AdMobError) => {
        if (settled) return
        settled = true
        if (reason !== 'Dismissed') {
          console.warn(
            `[ads] AdMob rewarded settled by ${reason} (slot=${slot}): ` +
              `${error?.message ?? error?.code ?? 'no details'}`,
          )
        }
        resolve()
      }
    })

    ;[rewardListener, dismissedListener, failedToLoadListener, failedToShowListener] = await Promise.all([
      AdMob.addListener(RewardAdPluginEvents.Rewarded, (_reward: AdMobRewardItem) => {
        rewarded = true
      }),
      AdMob.addListener(RewardAdPluginEvents.Dismissed, () => settle('Dismissed')),
      AdMob.addListener(RewardAdPluginEvents.FailedToLoad, (error: AdMobError) => {
        settle('FailedToLoad', error)
      }),
      AdMob.addListener(RewardAdPluginEvents.FailedToShow, (error: AdMobError) => {
        settle('FailedToShow', error)
      }),
    ])

    if (admobRewardPreloadInflight) {
      await admobRewardPreloadInflight
    }
    if (!admobRewardPreloaded) {
      await AdMob.prepareRewardVideoAd(options)
    }
    admobRewardPreloaded = false

    watchdogTimer = setTimeout(() => {
      settle('watchdog')
    }, AD_DISMISS_TIMEOUT_MS)

    if (bannerAppliedVisible) {
      await hideAdMobBannerInternal()
    }
    setAdMuted(true)
    await suspendForAd()
    audioSuspended = true

    void AdMob.showRewardVideoAd()
      .then((_reward: AdMobRewardItem) => {
        rewarded = true
      })
      .catch((error: AdMobError) => {
        settle('showRejected', error)
      })

    await finished
    return rewarded
  } catch (err) {
    admobRewardPreloaded = false
    console.warn(`[ads] AdMob rewarded failed (slot=${slot}):`, err)
    return false
  } finally {
    if (watchdogTimer) window.clearTimeout(watchdogTimer)
    if (audioSuspended) {
      await resumeFromAd()
      setAdMuted(false)
    }
    await Promise.allSettled([
      rewardListener?.remove(),
      dismissedListener?.remove(),
      failedToLoadListener?.remove(),
      failedToShowListener?.remove(),
    ])
    if (restoreBanner) {
      void syncBannerAd()
    }
    void preloadRewardedAd()
  }
}

// ---------------------------------------------------------------------------
// Mock 経路（オーバーレイ表示 → ユーザー操作待ち）
// ---------------------------------------------------------------------------

async function showMockRewarded(slot: AdSlotType): Promise<boolean> {
  // オーバーレイがまだマウントされていない場合は同期 confirm にフォールバック。
  // 起動直後（App.tsx が render する前）に広告を要求すると到達しうる経路。
  if (!registeredRewardedOverlay) {
    return showFallbackConfirm(slot)
  }

  return new Promise<boolean>((resolve) => {
    let settled = false
    registeredRewardedOverlay!(slot, (rewarded) => {
      if (settled) return
      settled = true
      resolve(rewarded)
    })
  })
}

async function showYahooRewarded(slot: AdSlotType): Promise<boolean> {
  const tagId = getYahooRewardedTagId(slot)
  if (!tagId) {
    console.warn(`[ads] Yahoo rewarded tag ID is empty (slot=${slot}); falling back to mock overlay`)
    return showMockRewarded(slot)
  }
  if (!registeredRewardedOverlay) {
    console.warn('[ads] Yahoo rewarded overlay is not registered')
    return false
  }

  return new Promise<boolean>((resolve) => {
    let settled = false
    registeredRewardedOverlay!(slot, (rewarded) => {
      if (settled) return
      settled = true
      resolve(rewarded)
    }, tagId)
  })
}

export function initializeYahooAdTag(elementId: string, tagId: string): boolean {
  if (typeof window === 'undefined') return false
  if (!tagId.trim()) return false
  try {
    window.YJ_YADS = window.YJ_YADS || { tasks: [] }
    window.YJ_YADS.tasks.push({
      yads_ad_ds: tagId,
      yads_parent_element: elementId,
    })
    return true
  } catch (err) {
    console.warn('[ads] Yahoo ad tag initialization failed:', err)
    return false
  }
}

async function confirmBeforeAd(slot: AdSlotType): Promise<boolean> {
  if (!registeredAdNekoPopup) {
    return showFallbackConfirm(slot)
  }

  return new Promise<boolean>((resolve) => {
    let settled = false
    registeredAdNekoPopup!(slot, (accepted) => {
      if (settled) return
      settled = true
      resolve(accepted)
    })
  })
}

async function showFallbackConfirm(slot: AdSlotType): Promise<boolean> {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
    // SSR / 非ブラウザ環境では物理的に表示不可。
    return false
  }
  const accepted = window.confirm(getAdRewardConfirmMessage(slot))
  if (!accepted) return false
  await new Promise<void>((resolve) => setTimeout(resolve, AD_MOCK_CONFIRM_DELAY_MS))
  return true
}
